import type {
  CpqState,
  CustomerCatalogRecord,
  FrozenCustomerSnapshot,
  Quote,
  QuoteCustomer,
} from './types';

/**
 * CustomerCatalogRecord is the only customer master.  This type is the
 * immutable, channel-scoped fact copied into a quote publication; it is not a
 * second customer entity or a write source.
 */
export function customerSnapshotForRecord(
  record: CustomerCatalogRecord,
): FrozenCustomerSnapshot {
  return {
    customerId: record.id,
    customerNo: record.customerNo,
    name: record.name,
    contact: record.contact,
    phone: record.phone,
  };
}

export function quoteCustomerForRecord(
  record: CustomerCatalogRecord,
): QuoteCustomer {
  return {
    customerId: record.id,
    ...(record.managerUserId ? { managerUserId: record.managerUserId } : {}),
    name: record.name,
    customerNo: record.customerNo,
    contact: record.contact,
    phone: record.phone,
  };
}

export function customerRecordForId(
  state: Pick<CpqState, 'catalog'>,
  customerId: string | undefined,
  options: { requireActive?: boolean } = {},
): CustomerCatalogRecord {
  if (!customerId || customerId.trim() !== customerId) {
    throw new Error('报价客户缺少稳定 customerId');
  }
  const customer = state.catalog.customers.find(
    (candidate) => candidate.id === customerId,
  );
  if (!customer) throw new Error(`客户 ${customerId} 不存在，禁止发布`);
  if (options.requireActive !== false && customer.status !== 'active') {
    throw new Error(`客户 ${customerId} 已停用，禁止发布`);
  }
  return customer;
}

/**
 * Resolves the current quote binding by exact customerId and verifies that
 * the quote's stored standard header facts still belong to that directory
 * record.  No name/phone lookup is permitted here.
 */
export function customerSnapshotForQuote(
  state: Pick<CpqState, 'catalog'>,
  quote: Quote,
): FrozenCustomerSnapshot {
  const customer = customerRecordForId(state, quote.customer.customerId);
  const expected = quoteCustomerForRecord(customer);
  const same =
    quote.customer.customerId === expected.customerId &&
    quote.customer.managerUserId === expected.managerUserId &&
    quote.customer.name === expected.name &&
    quote.customer.customerNo === expected.customerNo &&
    quote.customer.contact === expected.contact &&
    quote.customer.phone === expected.phone;
  if (!same) {
    throw new Error('报价客户绑定已过期，请重新选择客户目录记录');
  }
  return customerSnapshotForRecord(customer);
}
